import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNavBar from './PublicNavBar';
import { Helmet } from 'react-helmet-async';

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <Helmet>
        <title>TeAIch Suite — AI Tools for Teachers | Mark Faster, Plan Better</title>
        <meta name="description" content="AI marking and lesson planning tools for teachers. Mark BTEC, GCSE and A-Level work in seconds. First month £0.99." />
        <link rel="canonical" href="https://teaich.app/" />
      </Helmet>
      <PublicNavBar />

      {/* Hero */}
      <div className="flex items-center justify-center py-12 sm:py-16 md:py-20 px-4">
        <div className="text-center max-w-4xl">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6 text-coral-500 drop-shadow-[3px_3px_0_rgba(0,0,0,1)]">
            AI-POWERED TOOLS FOR TEACHERS WORLDWIDE
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-ink-900 mb-8 leading-relaxed">
            TeAIch Suite saves educators up to 6 hours per week on marking and lesson planning.
            Mark BTEC, GCSE, and A-Level work in seconds. Generate complete lesson plans instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/login"
              className="inline-block px-10 py-4 bg-coral-300 text-white rounded-lg font-bold text-lg hover:bg-coral-500 transition duration-200"
            >
              Get Started — First Month £0.99
            </a>
            <button
              onClick={() => navigate('/tools')}
              className="inline-block px-10 py-4 bg-white border-4 border-ink-900 text-ink-800 rounded-lg font-bold text-lg hover:bg-sand-50 transition duration-200"
            >
              See How It Works
            </button>
          </div>
          <p className="text-ink-500 text-sm mt-4">Then £8.99/month — cancel anytime</p>
        </div>
      </div>

      {/* Tools Overview */}
      <div className="py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* Assess */}
            <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-coral-300 rounded-full flex items-center justify-center overflow-hidden">
                <img src="/images/TeaichAssessAppLogo.png" alt="TeAIch Assess" className="w-14 h-14 object-contain" />
              </div>
              <h2 className="text-2xl font-bold text-ink-900 mb-3">TeAIch Assess</h2>
              <p className="text-ink-600 mb-6">Upload your mark scheme and student work. Get criterion-by-criterion AI feedback in seconds. Built for BTEC, GCSE, A-Level, and custom schemes.</p>
              <a href="/login" className="inline-block bg-coral-300 text-white px-8 py-3 rounded-lg font-bold hover:bg-coral-500 transition duration-200">
                Start Marking
              </a>
            </div>

            {/* Planner */}
            <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-coral-300 rounded-full flex items-center justify-center overflow-hidden">
                <img src="/images/TeaichPlannerAppLogo.png" alt="TeAIch Planner" className="w-14 h-14 object-contain" />
              </div>
              <h2 className="text-2xl font-bold text-ink-900 mb-3">TeAIch Planner</h2>
              <p className="text-ink-600 mb-6">Tell our AI your subject, year group, and topic. Get a complete, differentiated lesson plan with activities, resources, and assessment strategies instantly.</p>
              <a href="/login" className="inline-block bg-coral-300 text-white px-8 py-3 rounded-lg font-bold hover:bg-coral-500 transition duration-200">
                Start Planning
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="py-12 sm:py-16 px-4 bg-sand-50">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-ink-900 text-center mb-4">
              HOW IT WORKS — 3 SIMPLE STEPS
            </h2>
            <p className="text-center text-ink-600 text-lg mb-10">
              Whether you are marking work or planning lessons, TeAIch makes it simple
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { step: '1', title: 'CHOOSE YOUR TOOL', desc: 'Select TeAIch Assess for AI-powered marking or TeAIch Planner for instant lesson plans. Both tools work seamlessly together.' },
                { step: '2', title: 'UPLOAD YOUR CONTENT', desc: 'Upload your mark schemes and student work for assessment, or describe your lesson requirements for planning.' },
                { step: '3', title: 'RECEIVE QUALITY GUIDANCE', desc: 'Get detailed feedback, accurate grades, comprehensive lesson plans, and teaching resources in minutes — not hours.' },
              ].map(({ step, title, desc }) => (
                <div key={step} className="text-center">
                  <div className="w-16 h-16 bg-coral-300 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-2xl">{step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-ink-900 mb-3">{title}</h3>
                  <p className="text-ink-700">{desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <a href="/login" className="inline-block px-10 py-4 bg-coral-300 text-white rounded-lg font-bold text-lg hover:bg-coral-500 transition duration-200">
                Get Started Today
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="py-12 sm:py-16 px-4 bg-coral-100 border-t-4 border-b-4 border-ink-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-ink-900 text-center mb-10">
            TRUSTED BY EDUCATORS WORLDWIDE
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              { stat: '6 Hours', label: 'Saved per week on average' },
              { stat: '5 Minutes', label: 'To mark 30 assignments' },
              { stat: '£8.99', label: 'Per month — unlimited access' },
            ].map(({ stat, label }) => (
              <div key={stat} className="bg-white rounded-lg p-6 border-4 border-ink-900 text-center">
                <div className="text-4xl font-bold text-coral-500 mb-2">{stat}</div>
                <p className="text-ink-700 font-semibold">{label}</p>
              </div>
            ))}
          </div>
          <blockquote className="text-xl font-bold text-ink-900 leading-relaxed max-w-4xl mx-auto text-center">
            "TeAIch has revolutionised my teaching workflow. I now spend more time actually teaching and less time on administrative tasks."
          </blockquote>
          <p className="text-center text-ink-700 font-semibold mt-3">— Sarah M., Secondary School Teacher</p>
        </div>
      </div>

      {/* Features */}
      <div className="py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12 text-coral-500 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
            WHY EDUCATORS CHOOSE TEAICH
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'F', label: 'LIGHTNING FAST', desc: 'Mark 30 essays in under 5 minutes with consistent accuracy' },
              { icon: 'A', label: 'CONSISTENTLY ACCURATE', desc: 'Reliable, unbiased marking aligned to your criteria' },
              { icon: 'I', label: 'CLASS INSIGHTS', desc: 'Track criterion achievement rates and grade distributions across your whole class' },
              { icon: 'C', label: 'CURRICULUM ALIGNED', desc: 'Works with BTEC, GCSE, A-Level, and custom schemes' },
              { icon: 'P', label: 'QUICK PLANNING', desc: 'Create detailed lesson plans in minutes, not hours' },
              { icon: 'S', label: 'SECURE AND PRIVATE', desc: 'GDPR compliant with enterprise-grade security' },
              { icon: 'L', label: 'PERSONAL LIBRARY', desc: 'Save and organise all your plans and schemes' },
              { icon: 'V', label: 'GREAT VALUE', desc: 'Unlimited access to all tools for just £8.99/month' },
            ].map(({ label, desc }) => (
              <div key={label} className="bg-white rounded-lg p-6 text-center border-4 border-coral-300 hover:border-coral-500 transition duration-300 shadow-md">
                <h3 className="text-base font-bold text-ink-900 mb-2">{label}</h3>
                <p className="text-ink-600 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="py-12 sm:py-16 px-4 bg-sand-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-coral-500 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
            SIMPLE PRICING
          </h2>
          <p className="text-ink-700 text-lg mb-10">One plan. Everything included. No limits.</p>

          <div className="bg-white rounded-2xl border-4 border-coral-300 shadow-brutal p-8 md:p-12 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-coral-300 text-white px-6 py-2 rounded-full font-bold text-sm">TEAICH UNLIMITED</span>
            </div>
            <div className="mt-4 mb-2">
              <span className="text-6xl font-bold text-ink-900">£8.99</span>
              <span className="text-ink-500 text-lg">/month</span>
            </div>
            <p className="text-coral-600 font-semibold text-lg mb-8">First month just £0.99</p>
            <div className="text-left space-y-3 mb-8">
              {[
                'Unlimited AI marking with TeAIch Assess',
                'Unlimited lesson plans with TeAIch Planner',
                'Unlimited resource generation',
                'Class insights dashboard',
                'PDF upload and download support',
                'Save lessons and mark schemes to your library',
                'Priority email support',
              ].map((feature) => (
                <div key={feature} className="flex items-center">
                  <span className="text-coral-500 mr-3 font-bold">✓</span>
                  <span className="text-ink-700">{feature}</span>
                </div>
              ))}
            </div>
            <a
              href="/login"
              className="block bg-coral-300 text-white py-4 px-8 rounded-lg font-bold text-lg hover:bg-coral-500 transition duration-200"
            >
              Get Started — First Month £0.99
            </a>
            <p className="text-ink-500 text-sm mt-3">Then £8.99/month — cancel anytime</p>
          </div>

          <div className="mt-8 bg-white rounded-2xl border-4 border-ink-900 p-6">
            <h3 className="font-bold text-ink-900 mb-2">FOR ORGANISATIONS</h3>
            <p className="text-ink-600 text-sm mb-4">Need multiple seats or a bespoke plan for your school or department?</p>
            <button
              onClick={() => navigate('/contact')}
              className="bg-coral-300 text-white px-8 py-3 rounded-lg font-bold hover:bg-coral-500 transition duration-200"
            >
              Enquire Now
            </button>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12 text-coral-500 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
            FREQUENTLY ASKED QUESTIONS
          </h2>
          <div className="space-y-4">
            {[
              { q: 'How accurate is the AI marking?', a: 'Our AI uses advanced language models trained for educational assessment. It follows your specific marking criteria exactly, ensuring consistent and fair grading every time. We always recommend applying your professional judgement before sharing feedback with students.' },
              { q: 'What curriculum standards do you support?', a: 'TeAIch supports BTEC, GCSE, A-Level, and custom marking schemes. Our lesson planner works with all subjects and year groups from Primary through to Further Education.' },
              { q: 'Is my student data secure?', a: 'Yes — all data is encrypted and we are GDPR compliant. Student work is processed securely and not stored permanently. We never share or sell your data.' },
              { q: 'How does the first month offer work?', a: 'Your first month is just £0.99. After that it is £8.99/month. Cancel anytime from your account page.' },
              { q: 'What file formats do you support?', a: 'TeAIch Assess supports PDF files, images (JPG, PNG, GIF, BMP, WebP), and plain text. PDFs give the most accurate results.' },
            ].map(({ q, a }) => (
              <div key={q} className="bg-white border-4 border-ink-900 rounded-lg p-6">
                <h3 className="text-lg font-bold text-ink-900 mb-2">{q}</h3>
                <p className="text-ink-700">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-12 sm:py-16 px-4 bg-coral-100 border-t-4 border-ink-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-ink-900">
            READY TO TRANSFORM YOUR TEACHING?
          </h2>
          <p className="text-lg text-ink-700 mb-8 leading-relaxed">
            Join educators worldwide who use TeAIch to mark faster and plan better.
          </p>
          <a
            href="/login"
            className="inline-block px-12 py-4 bg-coral-500 text-white rounded-lg font-bold text-xl hover:bg-coral-600 transition duration-200 shadow-lg"
          >
            Get Started — First Month £0.99
          </a>
          <p className="text-ink-600 text-sm mt-4">Then £8.99/month — cancel anytime</p>
        </div>
      </div>

      <footer className="bg-sand-100 border-t-4 border-coral-300 py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div onClick={() => navigate('/')} className="cursor-pointer hover:opacity-80 transition-opacity">
              <img src="/images/craidle-logo.png" alt="Craidle Intelligence Group" className="h-10 sm:h-14 w-auto" />
            </div>
            <div className="flex space-x-4">
              <a href="YOUR_FACEBOOK_URL_HERE" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/teaich_app/" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="YOUR_TIKTOK_URL_HERE" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.27 8.27 0 004.84 1.56V6.81a4.85 4.85 0 01-1.07-.12z"/>
                </svg>
              </a>
              <a href="YOUR_LINKEDIN_URL_HERE" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              <button onClick={() => navigate('/feedback')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Feedback</button>
              <button onClick={() => navigate('/legal/privacy-policy')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Privacy Policy</button>
              <button onClick={() => navigate('/legal/terms-of-service')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Terms of Service</button>
              <button onClick={() => navigate('/legal/data-retention')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Data Retention</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;